import { ClickAwayListener, Paper, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type { Instance } from '@popperjs/core';

const CalendarPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  overflow: 'hidden',
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  // `overlay-shadows/floating-card` — the frame's own drop shadow.
  boxShadow: theme.shadows[1],
  backgroundColor: theme.palette.background.paper,
}));

/** The root element — popper.js intersects it with the viewport, so the result is the screen. */
const viewportBoundary = (): Element | undefined =>
  typeof document === 'undefined' ? undefined : document.documentElement;

export interface CalendarPopperProps {
  id: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  /** The field the popper belongs to: clicks on it are not "away". */
  fieldEl: HTMLElement | null;
  onClose: (reason: 'escape' | 'away') => void;
  onBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  label: string;
  /** Changes when the anchor's height does (a message appeared under the field): re-place. */
  layoutKey?: string;
  children: ReactNode;
  'data-testid'?: string;
}

/**
 * The floating card the grid opens in, BELOW the field and never over it (the frame's own
 * note: "try not to cover save button and input itself").
 *
 * NON-MODAL ON PURPOSE. A MUI `Popover` lays an invisible backdrop over the whole page, so
 * the field under it could not be clicked or typed into while the calendar is open — and
 * typing a date beside the open calendar is half of what this control is for. A `Popper`
 * leaves the page live; click-away and Escape close it instead.
 *
 * INSIDE A MODAL IT RENDERS IN PLACE. A Menu or Dialog traps focus inside its own DOM, so a
 * calendar portalled to `<body>` would have focus yanked back the moment a day was
 * focused. When the field sits in a `.MuiModal-root` the popper renders next to it instead,
 * positioned `fixed` so the modal's scroll box does not clip it.
 *
 * Pressing on the card never takes focus away from the input being typed in — the
 * `mousedown` default is cancelled — so a half-typed date is not committed by a click on
 * a day that is about to replace it anyway.
 */
export const CalendarPopper = forwardRef<HTMLDivElement, CalendarPopperProps>(
  function CalendarPopper(
    {
      id,
      open,
      anchorEl,
      fieldEl,
      onClose,
      onBlur,
      label,
      layoutKey,
      children,
      'data-testid': testId,
    },
    ref,
  ) {
    const inModal = useMemo(() => Boolean(anchorEl?.closest('.MuiModal-root')), [anchorEl]);
    const popperRef = useRef<Instance | null>(null);
    useEffect(() => {
      void popperRef.current?.update();
    }, [layoutKey]);

    /* The grid's keys stop here. Rendered inside a Menu, its arrows would otherwise also
       drive the Menu's own item navigation (focus jumps out of the grid and the calendar
       closes), and Escape would close the Menu along with the calendar. */
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Tab') return;
      event.stopPropagation();
      if (event.key === 'Escape') onClose('escape');
    };

    return (
      <Popper
        open={open && Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement='bottom-start'
        popperRef={popperRef}
        disablePortal={inModal}
        popperOptions={inModal ? { strategy: 'fixed' } : undefined}
        /* The VIEWPORT is the boundary to stay inside. popper.js defaults to the nearest
           scrolling ancestors, which inside a Menu is the Menu's own small scroll box —
           the card cannot fit in it, so it was left hanging off the screen edge. */
        modifiers={[
          { name: 'offset', options: { offset: [0, 4] } },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
              boundary: viewportBoundary(),
            },
          },
          { name: 'preventOverflow', options: { padding: 8, boundary: viewportBoundary() } },
        ]}
        sx={(theme) => ({ zIndex: theme.zIndex.modal + 1 })}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            if (fieldEl && event.target instanceof Node && fieldEl.contains(event.target)) return;
            onClose('away');
          }}
        >
          <CalendarPaper
            ref={ref}
            id={id}
            role='dialog'
            aria-label={label}
            elevation={0}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            onMouseDown={(event) => event.preventDefault()}
            // The request-template block keeps its focus styling while its own picker is open.
            data-request-block-focus-retainer=''
            data-testid={testId}
          >
            {children}
          </CalendarPaper>
        </ClickAwayListener>
      </Popper>
    );
  },
);
